import { BaseModel } from './base.model';
import { OrderItemModel } from './order-item.model';
import { findInventoryForItem, getCreditStoreFromMatrix } from '../../../helpers';
import { CUSTOM } from '@pulse/constants/claims';
import type { Orders } from '@database/types';

export class ClaimItemModel extends BaseModel<any> {
  get id() {
    return this._data.id;
  }

  get photo() {
    return this._data.imageUrl;
  }

  get validation() {
    return this._data.validation ?? null;
  }

  // True when this item was rejected at validation as not sold by this store.
  get isRejected() {
    return this._data.validation?.validationOutcome === 'REJECTED';
  }

  get resolution() {
    return this?.claim?.resolutions.find(r => r.id === this.resolutionId);
  }

  get creditStore() {
    if (this.claim.isOta && !this.claim.includeOtasInReimbursements) return false;
    if (this.resolution) return this.resolution.creditStore;

    return getCreditStoreFromMatrix(this.claim?.coverageRules, this.claimType, this.claim?.policy?.policyType);
  }

  set creditStore(creditStore) {
    this._data.creditStore = creditStore;
  }

  get details() {
    return this._data.details;
  }

  get productImage() {
    return this.photo;
  }

  get claim() {
    return this._data.claim;
  }

  get order() {
    return this.claim.order;
  }

  get quantity() {
    return this._data.quantity;
  }

  get resolutionId() {
    return this._data.resolutionId;
  }

  get refundedQuantity() {
    return this._data?.orderItem?.refundedQuantity;
  }

  get claimSubtype() {
    return this._data.claimSubtype;
  }

  set claimSubtype(claimSubtype) {
    this._data.claimSubtype = claimSubtype;
  }

  get validQuantity() {
    const quantity = this._data?.orderItem?.quantity;
    const refundedQuantity = this.refundedQuantity;
    const claimQuantity = this.quantity;
    const remainingQuantity = quantity - refundedQuantity;

    if (remainingQuantity === 0 || claimQuantity > remainingQuantity) {
      return remainingQuantity;
    }

    return claimQuantity;
  }

  get title() {
    return this._data.name;
  }

  get orderId() {
    return this._data.orderId;
  }

  get productName() {
    return this.title;
  }

  get variation() {
    return '';
  }

  get orderItem() {
    const items: Orders.OrderItem[] = this.claim.order.orderItems || [];
    const found = items.find(
      item => item.sourceItemId === this._data.sourceItemId && item.sourceVariantId === this._data.sourceVariantId,
    );
    return found ? new OrderItemModel(found) : null;
  }

  get currency() {
    return this.claim?.currency || this.claim?.order?.currency || 'USD';
  }

  get price() {
    if (!this.claim.order.orderItems || this.claim.order.orderItems.length === 0) {
      return 0;
    }

    return Number(this.orderItem?.price) || 0;
  }

  get sku() {
    return this._data.sku;
  }

  get selectedOptions() {
    return this?.orderItem?.selectedOptions ?? [];
  }

  get properties() {
    return this?.orderItem?.properties ?? [];
  }

  get images() {
    return this._data.images.filter((item: { url: string }) => /\.(jpg|jpeg|png|gif|bmp|webp|heic)$/i.test(item.url));
  }

  get videos(): { url: string }[] {
    return this._data.images.filter((item: { url: string }) => /\.(mp4|mov)$/i.test(item.url));
  }

  get issue() {
    return {
      quantity: this.quantity,
      details: this.details,
      issueType: this.claimType,
      images: this.images,
    };
  }

  get discount() {
    if (!this.claim.order.orderItems || this.claim.order.orderItems.length === 0) {
      return 0;
    }

    return Number(this.orderItem?.discount) || 0;
  }

  get name() {
    return this.title;
  }

  get sourceVariantId() {
    return this._data.sourceVariantId;
  }
  get priceAfterDiscounts() {
    if (!this.claim.order.orderItems || this.claim.order.orderItems.length === 0) {
      return 0;
    }
    const price = Number(this.orderItem?.price) - Number(this.orderItem?.discount);

    return +price.toFixed(3);
  }

  get priceInDollars() {
    if (!this.claim.order.orderItems || this.claim.order.orderItems.length === 0) {
      return 0;
    }
    const price = (Number(this.orderItem?.price) - Number(this.orderItem?.discount)) * this.exchangeRate;

    return price.toFixed(3);
  }

  get tax() {
    return parseFloat(this.orderItem?.tax ?? 0).toFixed(3);
  }

  get exchangeRate() {
    return this.claim.order.exchangeRate;
  }

  get claimType() {
    return this._data.claimType || 'Unknown';
  }

  set claimType(claimType) {
    this._data.claimType = claimType;
  }

  get quantityType() {
    return this._data?.quantityType;
  }

  set quantityType(quantityType) {
    if (quantityType !== CUSTOM) this._data.resolutionQuantity = +quantityType;
    this._data.quantityType = quantityType;
  }

  get reimbursementType() {
    return this._data?.reimbursementType;
  }

  set reimbursementType(reimbursementType) {
    this._data.reimbursementType = reimbursementType;
  }

  get sourceProductId() {
    return this._data.sourceProductId;
  }

  get resolutionRequested() {
    return this._data.resolutionRequested;
  }

  /**
   * ENG-6073. The customer would rather have this item than the payout they chose, if it is back in
   * stock when the claim is approved. Display only for now — nothing acts on it yet.
   */
  get reshipIfRestocked() {
    return this._data.reshipIfRestocked ?? false;
  }

  get resolutionType() {
    return this._data.resolutionType ?? null;
  }

  set resolutionType(resolutionType) {
    this._data.resolutionType = resolutionType;
    if (!resolutionType) {
      this._data.percentage = null;
      this._data.resolutionQuantity = null;
      this._data.quantityType = null;
    }
  }

  get inventory() {
    return findInventoryForItem(this?.order?.inventory, this);
  }

  set inventory(inventory) {
    this._data.inventory = inventory;
  }

  get resolutionQuantity() {
    return this._data.resolutionQuantity;
  }

  set resolutionQuantity(quantity) {
    this._data.resolutionQuantity = quantity > 0 ? +quantity : null;
  }

  set percentage(percentage) {
    this._data.percentage = +percentage;
  }

  get percentage() {
    return this._data.percentage;
  }

  get resolvedAmount() {
    return this._data.resolvedAmount;
  }

  set resolvedAmount(flatAmount) {
    this._data.resolvedAmount = +flatAmount;
  }

  get sourceItemId() {
    return this._data.sourceItemId;
  }

  get sourceOrderId() {
    return this._data.sourceOrderId;
  }

  get claimItemMetadata() {
    return this._data.claimItemMetadata;
  }

  /**
   * The customer's ORIGINAL product identity when in-store validation swapped or substituted this
   * item, or null when it was validated as-is (or rejected). Sourced from the validation record's
   * snapshot — the `{ originalName, originalSku }` shape the claim view renders.
   */
  get originalSelection() {
    const v = this._data.validation;
    if (!v || !(v.productChanged || v.isSubstitute)) return null;

    return { originalName: v.originalProductName, originalSku: v.originalSku };
  }

  /**
   * The product the CUSTOMER accepted at filing when their own could not be identified (ENG-5874),
   * or null on an ordinary claim.
   *
   * Not to be confused with `originalSelection` above, which is the opposite direction and a
   * different actor: that one is the agent swapping the product during validation. This one is
   * chosen before anyone has looked at the claim, and the item it sits on is still the customer's
   * own description.
   */
  get closestMatch() {
    const meta = this._data.claimItemMetadata;
    if (!meta?.replacementSourceProductId) return null;

    // Ids only — name, sku and price live in the catalog and are resolved by whoever renders this.
    return { sourceProductId: meta.replacementSourceProductId, sourceVariantId: meta.replacementSourceVariantId };
  }
}
